// W41.0 E5 — the mascot component.
//
// The four blobs and their generation parameters live in `./mascots.ts`; this
// file only renders. Same reason as `Icon.tsx`: `eslint --max-warnings=0`
// enforces `react-refresh/only-export-components`, and a file exporting both a
// component and plain data falls back to a full reload on every edit.

import { MASCOTS } from "./mascots";
import type { MascotName } from "./mascots";

export type { MascotName };

export interface MascotProps {
  name: MascotName;
  /** Rendered size in px. The viewBox is 200², so the face scales with it. */
  size?: number;
  /**
   * Body fill override. The empty state passes `#fff` so a lilac mascot reads on
   * a lilac block — the "a character never shares its card's colour" rule
   * (DESIGN.md §18.0). Defaults to the mascot's own colour.
   */
  fill?: string;
  className?: string;
}

export function Mascot({ name, size = 112, fill, className }: MascotProps) {
  const m = MASCOTS[name];
  const body = fill ?? m.spec.fill;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      className={className}
      // A mascot is never decorative — it replaces `EmptyState`'s icon slot and
      // is the only thing naming the emotion on that screen.
      role="img"
      aria-label={m.label}
    >
      <path fill={body} d={m.d} />
      {m.spec.face === "dot-eyes" ? (
        <>
          <circle cx="82" cy="94" r="10" fill="#1C1C1E" />
          <circle cx="118" cy="94" r="10" fill="#1C1C1E" />
          <circle cx="85" cy="90" r="3.2" fill="#fff" />
          <circle cx="121" cy="90" r="3.2" fill="#fff" />
          <path
            d="M88 120 Q100 133 112 120"
            fill="none"
            stroke="#1C1C1E"
            strokeWidth={7}
            strokeLinecap="round"
          />
        </>
      ) : (
        <>
          <path d="M72 95 q10 -10 20 0" fill="none" stroke="#1C1C1E" strokeWidth={7} strokeLinecap="round" />
          <path d="M108 95 q10 -10 20 0" fill="none" stroke="#1C1C1E" strokeWidth={7} strokeLinecap="round" />
          <path d="M89 124 q11 10 22 0" fill="none" stroke="#1C1C1E" strokeWidth={7} strokeLinecap="round" />
        </>
      )}
      <ellipse cx="60" cy="116" rx="12" ry="7" fill="#fff" opacity=".5" />
      <ellipse cx="140" cy="116" rx="12" ry="7" fill="#fff" opacity=".5" />
    </svg>
  );
}
